import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import API from "src/API2";
import "./settings.css";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcCash    = () => <Svg><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M6 12h.01M18 12h.01" /></Svg>;
const IcCard    = () => <Svg><rect x="2" y="5" width="20" height="14" rx="2" ry="2" /><line x1="2" y1="10" x2="22" y2="10" /></Svg>;
const IcCheck   = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcInfo    = () => <Svg size={14}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></Svg>;

/* Booking-mode toggle (single setting) */
const SettingToggle = ({
  icon, title, hint, value, dirty, saving,
  onToggle, onSave,
}) => {
  const isOn = String(value) === "true" || value === true;
  return (
    <article className={`st-card ${isOn ? "is-on" : "is-off"}`}>
      <header className="st-card__head">
        <span className="st-card__icon">{icon}</span>
        <div>
          <h3>{title}</h3>
          <p>{hint}</p>
        </div>
      </header>

      <div className="st-card__body">
        <button
          type="button"
          className={`st-switch ${isOn ? "is-on" : ""}`}
          onClick={onToggle}
          aria-pressed={isOn}
          aria-label={`Toggle ${title}`}
        >
          <span className="st-switch__knob" />
        </button>
        <div className="st-card__state">
          <span className={`st-status st-status--${isOn ? "on" : "off"}`}>
            {isOn ? "Enabled" : "Disabled"}
          </span>
          {dirty && <span className="st-dirty">Unsaved change</span>}
        </div>
      </div>

      <footer className="st-card__foot">
        <button
          type="button"
          className="st-btn st-btn--primary"
          onClick={onSave}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : (<><IcCheck /> Save</>)}
        </button>
      </footer>
    </article>
  );
};

const Settings = () => {
  /* params1 = configs[0] = "offline-style" toggle (booking without online payment)
     params  = configs[1] = "online" payment toggle */
  const [params, setParams] = useState(null);
  const [params1, setParams1] = useState(null);

  const [online, setOnline] = useState("false");
  const [offline, setOffline] = useState("true");

  /* Snapshot of last-saved values to compute dirty state */
  const [savedOnline, setSavedOnline] = useState("false");
  const [savedOffline, setSavedOffline] = useState("true");

  const [loading, setLoading] = useState(true);
  const [savingOnline, setSavingOnline] = useState(false);
  const [savingOffline, setSavingOffline] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get("config")
      .then((res) => {
        const c0 = res.data.configs[0];
        const c1 = res.data.configs[1];
        setParams1(c0);
        setOffline(String(c0.value));
        setSavedOffline(String(c0.value));
        setParams(c1);
        setOnline(String(c1.value));
        setSavedOnline(String(c1.value));
      })
      .catch(() => toast.error("Could not load settings"))
      .finally(() => setLoading(false));
  }, []);

  const updateOnline = () => {
    if (!params) return;
    setSavingOnline(true);
    toast.promise(
      API.put("config/" + params.id, { value: online })
        .then(() => setSavedOnline(online))
        .finally(() => setSavingOnline(false)),
      { loading: "Saving…", success: "Online payment updated", error: "Could not save" },
    );
  };

  const updateOffline = () => {
    if (!params1) return;
    setSavingOffline(true);
    toast.promise(
      API.put("config/" + params1.id, { value: offline })
        .then(() => setSavedOffline(offline))
        .finally(() => setSavingOffline(false)),
      { loading: "Saving…", success: "Booking mode updated", error: "Could not save" },
    );
  };

  const onlineDirty = String(online) !== String(savedOnline);
  const offlineDirty = String(offline) !== String(savedOffline);

  const onlineOn = String(online) === "true";
  const offlineOn = String(offline) === "true";

  return (
    <div className="st-page">
      <header className="st-hero">
        <div>
          <span className="st-hero__eyebrow">System</span>
          <h1 className="st-hero__title">Settings</h1>
          <p className="st-hero__sub">
            Control how reservations can be paid for. These switches affect every
            booking made on your website — review carefully before saving.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="st-loading">Loading settings…</div>
      ) : (
        <div className="st-grid">
          <SettingToggle
            icon={<IcCard />}
            title="Online payment"
            hint="When enabled, guests can pay for their reservation online at checkout. Disable to handle all payments manually."
            value={online}
            dirty={onlineDirty}
            saving={savingOnline}
            onToggle={() => setOnline(String(online === "true" ? false : true))}
            onSave={updateOnline}
          />

          <SettingToggle
            icon={<IcCash />}
            title="Reservations without online payment"
            hint="When enabled, guests can book without paying immediately — payment will be collected at the property. Disable to require online payment for every booking."
            value={offline}
            dirty={offlineDirty}
            saving={savingOffline}
            onToggle={() => setOffline(String(offline === "true" ? false : true))}
            onSave={updateOffline}
          />

          {/* Read-only summary tile */}
          <article className="st-card st-card--summary">
            <header className="st-card__head">
              <span className="st-card__icon st-card__icon--neutral"><IcInfo /></span>
              <div>
                <h3>Booking flow summary</h3>
                <p>How guests will experience checkout right now.</p>
              </div>
            </header>
            <ul className="st-summary">
              <li>
                <span>Online payment</span>
                <strong className={onlineOn ? "is-on" : "is-off"}>
                  {onlineOn ? "Available" : "Unavailable"}
                </strong>
              </li>
              <li>
                <span>Pay-at-property</span>
                <strong className={offlineOn ? "is-on" : "is-off"}>
                  {offlineOn ? "Available" : "Unavailable"}
                </strong>
              </li>
              <li>
                <span>Net effect</span>
                <strong className={onlineOn || offlineOn ? "is-on" : "is-off"}>
                  {onlineOn && offlineOn
                    ? "Both options offered to guests"
                    : onlineOn
                    ? "Online payment only"
                    : offlineOn
                    ? "Pay-at-property only"
                    : "Bookings disabled"}
                </strong>
              </li>
            </ul>
          </article>
        </div>
      )}
    </div>
  );
};

export default Settings;
